import { useState, useEffect, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Send, Search, Plus, Users, Edit2, Trash2 } from 'lucide-react';
import { useToast } from '@/components/ui/use-toast';
import { Card } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';

interface Message {
  id: string;
  sender_id: string;
  conversation_id: string;
  content: string;
  created_at: string;
  sender?: {
    first_name: string;
    last_name: string;
  };
}

interface Conversation {
  id: string;
  name: string | null;
  is_group: boolean;
  created_at: string;
  updated_at: string;
  participants?: Participant[];
  last_message?: string;
}

interface Participant {
  id: string;
  user_id: string;
  profiles: {
    first_name: string;
    last_name: string;
  };
}

interface Contact {
  id: string;
  first_name: string;
  last_name: string;
}

export function InAppMessaging() {
  const { user } = useAuth();
  const { toast } = useToast();
  const [messages, setMessages] = useState<Message[]>([]);
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [selectedConversation, setSelectedConversation] = useState<Conversation | null>(null);
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [newMessage, setNewMessage] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [showNewConversation, setShowNewConversation] = useState(false);
  const [selectedUsers, setSelectedUsers] = useState<string[]>([]);
  const [groupName, setGroupName] = useState('');
  const [showRenameDialog, setShowRenameDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [conversationToEdit, setConversationToEdit] = useState<Conversation | null>(null);
  const [newConversationName, setNewConversationName] = useState('');
  const scrollRef = useRef<HTMLDivElement>(null);
  const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    if (user) {
      loadConversations();
      loadContacts();
      subscribeToMessages();
    }
  }, [user]);

  useEffect(() => {
    if (selectedConversation) {
      loadMessages(selectedConversation.id);
    }
  }, [selectedConversation]);

  // Debounced search
  useEffect(() => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    searchTimeoutRef.current = setTimeout(() => {
      if (searchQuery) {
        loadContacts();
      }
    }, 300);

    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [searchQuery]);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages]);

  const subscribeToMessages = () => {
    const channel = supabase
      .channel('messages')
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'messages',
        },
        (payload) => {
          const newMsg = payload.new as Message;
          if (selectedConversation && newMsg.conversation_id === selectedConversation.id) {
            setMessages((prev) => [...prev, newMsg]);
          }
          // Refresh conversations list
          loadConversations();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  };

  const loadConversations = async () => {
    const { data: convData, error } = await supabase
      .from('conversations')
      .select('*')
      .order('updated_at', { ascending: false});

    if (error) {
      console.error('Error loading conversations:', error);
      return;
    }

    // Load participants and messages for each conversation
    const conversationsWithDetails = await Promise.all(
      (convData || []).map(async (conv) => {
        // Get participants
        const { data: participantRows } = await supabase
          .from('conversation_participants')
          .select('id, user_id')
          .eq('conversation_id', conv.id);

        // Get profiles for participants
        const participants = await Promise.all(
          (participantRows || []).map(async (p) => {
            const { data: profile } = await supabase
              .from('profiles')
              .select('first_name, last_name')
              .eq('id', p.user_id)
              .single();

            return {
              id: p.id,
              user_id: p.user_id,
              profiles: profile || { first_name: '', last_name: '' },
            };
          })
        );

        // Get last message
        const { data: lastMsg } = await supabase
          .from('messages')
          .select('content')
          .eq('conversation_id', conv.id)
          .order('created_at', { ascending: false })
          .limit(1)
          .maybeSingle();

        return {
          ...conv,
          participants,
          last_message: lastMsg?.content || 'No messages yet',
        };
      })
    );

    setConversations(conversationsWithDetails);
  };

  const loadContacts = async () => {
    let query = supabase
      .from('profiles')
      .select('id, first_name, last_name')
      .neq('id', user?.id);

    if (searchQuery) {
      query = query.or(`first_name.ilike.%${searchQuery}%,last_name.ilike.%${searchQuery}%`);
    }

    const { data, error } = await query.limit(50);

    if (error) {
      console.error('Error loading contacts:', error);
      return;
    }

    setContacts(data || []);
  };

  const loadMessages = async (conversationId: string) => {
    const { data, error } = await supabase
      .from('messages')
      .select(`
        *,
        sender:profiles!messages_sender_id_fkey(first_name, last_name)
      `)
      .eq('conversation_id', conversationId)
      .order('created_at', { ascending: true });

    if (error) {
      console.error('Error loading messages:', error);
      return;
    }

    setMessages(data || []);
  };

  const createConversation = async () => {
    if (selectedUsers.length === 0) {
      toast({
        title: 'Error',
        description: 'Please select at least one person',
        variant: 'destructive',
      });
      return;
    }

    setLoading(true);
    const isGroup = selectedUsers.length > 1;
    
    // Create conversation
    const { data: conv, error: convError } = await supabase
      .from('conversations')
      .insert({
        name: isGroup ? groupName || 'Group Chat' : null,
        is_group: isGroup,
        created_by: user?.id,
      })
      .select()
      .single();

    if (convError || !conv) {
      toast({
        title: 'Error',
        description: 'Failed to create conversation',
        variant: 'destructive',
      });
      setLoading(false);
      return;
    }

    // Add participants (including current user)
    const participants = [user?.id, ...selectedUsers].map(userId => ({
      conversation_id: conv.id,
      user_id: userId,
    }));

    const { error: partError } = await supabase
      .from('conversation_participants')
      .insert(participants);

    if (partError) {
      toast({
        title: 'Error',
        description: 'Failed to add participants',
        variant: 'destructive',
      });
    } else {
      setShowNewConversation(false);
      setSelectedUsers([]);
      setGroupName('');
      await loadConversations();
      
      // Find and select the newly created conversation with full data
      const { data: fullConv } = await supabase
        .from('conversations')
        .select('*')
        .eq('id', conv.id)
        .single();
      
      if (fullConv) {
        setSelectedConversation(fullConv);
      }
    }
    setLoading(false);
  };

  const sendMessage = async () => {
    if (!newMessage.trim() || !selectedConversation) return;

    setLoading(true);
    const { error } = await supabase.from('messages').insert({
      sender_id: user?.id,
      conversation_id: selectedConversation.id,
      content: newMessage.trim(),
    });

    if (error) {
      toast({
        title: 'Error',
        description: 'Failed to send message',
        variant: 'destructive',
      });
    } else {
      setNewMessage('');
      loadMessages(selectedConversation.id);
      // Update conversation timestamp
      await supabase
        .from('conversations')
        .update({ updated_at: new Date().toISOString() })
        .eq('id', selectedConversation.id);
      loadConversations();
    }
    setLoading(false);
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const getConversationTitle = (conv: Conversation) => {
    if (conv.name) return conv.name;
    if (!conv.participants) return 'Conversation';
    
    const otherParticipants = conv.participants.filter(p => p.user_id !== user?.id);
    if (otherParticipants.length === 0) return 'Me';
    if (otherParticipants.length === 1) {
      const p = otherParticipants[0];
      return `${p.profiles.first_name} ${p.profiles.last_name}`;
    }
    return otherParticipants.map(p => p.profiles.first_name).join(', ');
  };

  const handleRenameConversation = async () => {
    if (!conversationToEdit || !newConversationName.trim()) return;

    const { error } = await supabase
      .from('conversations')
      .update({ name: newConversationName.trim() })
      .eq('id', conversationToEdit.id);

    if (error) {
      toast({
        title: 'Error',
        description: 'Failed to rename conversation',
        variant: 'destructive',
      });
    } else {
      // Update conversations list immediately
      setConversations(prev => prev.map(conv => 
        conv.id === conversationToEdit.id 
          ? { ...conv, name: newConversationName.trim() }
          : conv
      ));
      
      if (selectedConversation?.id === conversationToEdit.id) {
        setSelectedConversation({ ...selectedConversation, name: newConversationName.trim() });
      }
      
      toast({
        title: 'Success',
        description: 'Conversation renamed',
      });
    }
    setShowRenameDialog(false);
    setNewConversationName('');
    setConversationToEdit(null);
  };

  const handleDeleteConversation = async () => {
    if (!conversationToEdit) return;

    const deletedId = conversationToEdit.id;

    // Delete all messages first
    await supabase
      .from('messages')
      .delete()
      .eq('conversation_id', deletedId);

    // Delete all participants
    await supabase
      .from('conversation_participants')
      .delete()
      .eq('conversation_id', deletedId);

    // Delete conversation
    const { error } = await supabase
      .from('conversations')
      .delete()
      .eq('id', deletedId);

    if (error) {
      toast({
        title: 'Error',
        description: 'Failed to delete conversation',
        variant: 'destructive',
      });
      setShowDeleteDialog(false);
      setConversationToEdit(null);
      return;
    }

    // Clear selected conversation if it was the one deleted
    if (selectedConversation?.id === deletedId) {
      setSelectedConversation(null);
      setMessages([]);
    }
    
    // Update conversations list immediately
    setConversations(prev => prev.filter(conv => conv.id !== deletedId));
    
    toast({
      title: 'Success',
      description: 'Conversation deleted',
    });
    
    setShowDeleteDialog(false);
    setConversationToEdit(null);
    
    // Refresh in background
    loadConversations();
  };

  return (
    <div className="flex h-[600px] border rounded-lg overflow-hidden">
      {/* Conversations sidebar */}
      <div className="w-1/3 border-r flex flex-col">
        <div className="p-4 border-b space-y-2">
          <Dialog open={showNewConversation} onOpenChange={setShowNewConversation}>
            <DialogTrigger asChild>
              <Button 
                className="w-full transition-all hover:scale-105" 
                variant="outline"
                aria-label="Start a new conversation"
              >
                <Plus className="h-4 w-4 mr-2" />
                New Conversation
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-md" aria-describedby="new-conversation-description">
              <DialogHeader>
                <DialogTitle>Start a Conversation</DialogTitle>
                <p id="new-conversation-description" className="sr-only">
                  Search for users to start a new conversation or group chat
                </p>
              </DialogHeader>
              <div className="space-y-4">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder="Search users..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-9"
                    aria-label="Search for users"
                  />
                </div>
                {selectedUsers.length > 1 && (
                  <Input
                    placeholder="Group name (optional)"
                    value={groupName}
                    onChange={(e) => setGroupName(e.target.value)}
                    aria-label="Group name"
                  />
                )}
                <ScrollArea className="h-[300px] border rounded p-2">
                  {contacts.map((contact) => (
                    <div 
                      key={contact.id} 
                      className="flex items-center gap-2 p-2 hover:bg-muted rounded transition-colors focus-within:ring-2 focus-within:ring-primary"
                    >
                      <Checkbox
                        id={`contact-${contact.id}`}
                        checked={selectedUsers.includes(contact.id)}
                        onCheckedChange={(checked) => {
                          if (checked) {
                            setSelectedUsers([...selectedUsers, contact.id]);
                          } else {
                            setSelectedUsers(selectedUsers.filter(id => id !== contact.id));
                          }
                        }}
                        aria-label={`Select ${contact.first_name} ${contact.last_name}`}
                      />
                      <Label 
                        htmlFor={`contact-${contact.id}`}
                        className="flex items-center gap-2 flex-1 cursor-pointer"
                      >
                        <Avatar className="h-8 w-8">
                          <AvatarFallback className="text-xs">
                            {contact.first_name[0]}{contact.last_name[0]}
                          </AvatarFallback>
                        </Avatar>
                        <span>{contact.first_name} {contact.last_name}</span>
                      </Label>
                    </div>
                  ))}
                </ScrollArea>
                <Button 
                  onClick={createConversation} 
                  disabled={loading || selectedUsers.length === 0} 
                  className="w-full transition-all hover:scale-105 disabled:hover:scale-100"
                  aria-label={selectedUsers.length > 1 ? 'Create group chat' : 'Start one-on-one chat'}
                >
                  {loading && <span className="mr-2 h-4 w-4 animate-spin rounded-full border-2 border-background border-t-transparent" />}
                  {selectedUsers.length > 1 ? 'Create Group' : 'Start Chat'}
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>
        <ScrollArea className="flex-1">
          {conversations.map((conv) => (
            <Card
              key={conv.id}
              className={`p-4 cursor-pointer hover:bg-muted/50 transition-all border-0 rounded-none focus-within:ring-2 focus-within:ring-primary animate-fade-in ${
                selectedConversation?.id === conv.id ? 'bg-muted' : ''
              }`}
              role="button"
              tabIndex={0}
              aria-label={`Conversation with ${getConversationTitle(conv)}`}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  setSelectedConversation(conv);
                }
              }}
            >
              <div className="flex items-center gap-3" onClick={() => setSelectedConversation(conv)}>
                <Avatar>
                  <AvatarFallback>
                    {conv.is_group ? <Users className="h-4 w-4" /> : getConversationTitle(conv)[0]}
                  </AvatarFallback>
                </Avatar>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between">
                    <p className="font-medium truncate">
                      {getConversationTitle(conv)}
                    </p>
                    <div className="flex items-center gap-1">
                      {conv.is_group && <Users className="h-3 w-3 text-muted-foreground" aria-label="Group conversation" />}
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            className="h-6 w-6 p-0 hover:scale-110 transition-transform focus:ring-2 focus:ring-primary"
                            aria-label="Conversation options"
                          >
                            <span className="sr-only">Menu</span>
                            <div className="flex gap-0.5">
                              <span className="h-1 w-1 rounded-full bg-current" />
                              <span className="h-1 w-1 rounded-full bg-current" />
                              <span className="h-1 w-1 rounded-full bg-current" />
                            </div>
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="z-50 bg-background">
                          <DropdownMenuItem
                            onClick={(e) => {
                              e.stopPropagation();
                              setConversationToEdit(conv);
                              setNewConversationName(conv.name || '');
                              setShowRenameDialog(true);
                            }}
                            className="cursor-pointer focus:bg-muted"
                          >
                            <Edit2 className="h-4 w-4 mr-2" />
                            Rename
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            onClick={(e) => {
                              e.stopPropagation();
                              setConversationToEdit(conv);
                              setShowDeleteDialog(true);
                            }}
                            className="text-destructive cursor-pointer focus:bg-destructive/10"
                          >
                            <Trash2 className="h-4 w-4 mr-2" />
                            Delete
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  </div>
                  <p className="text-xs text-muted-foreground truncate">
                    {conv.last_message}
                  </p>
                </div>
              </div>
            </Card>
          ))}
        </ScrollArea>
      </div>

      {/* Messages area */}
      <div className="flex-1 flex flex-col">
        {selectedConversation ? (
          <>
            <div className="p-4 border-b animate-fade-in">
              <h3 className="font-semibold">
                {getConversationTitle(selectedConversation)}
              </h3>
              {selectedConversation.is_group && selectedConversation.participants && (
                <p className="text-xs text-muted-foreground">
                  {selectedConversation.participants.length} participants
                </p>
              )}
            </div>
            <ScrollArea className="flex-1 p-4" ref={scrollRef} aria-live="polite">
              {messages.map((message, index) => (
                <div
                  key={message.id}
                  className={`mb-4 flex animate-fade-in ${
                    message.sender_id === user?.id ? 'justify-end' : 'justify-start'
                  }`}
                  style={{ animationDelay: `${index * 0.05}s` }}
                >
                  <div
                    className={`max-w-[70%] rounded-lg p-3 transition-all hover:scale-[1.02] ${
                      message.sender_id === user?.id
                        ? 'bg-primary text-primary-foreground'
                        : 'bg-muted'
                    }`}
                    role="article"
                    aria-label={`Message from ${message.sender_id === user?.id ? 'you' : `${message.sender?.first_name} ${message.sender?.last_name}`}`}
                  >
                    {selectedConversation.is_group && message.sender_id !== user?.id && (
                      <p className="text-xs font-semibold mb-1">
                        {message.sender?.first_name} {message.sender?.last_name}
                      </p>
                    )}
                    <p className="text-sm">{message.content}</p>
                    <p className="text-xs mt-1 opacity-70">
                      {new Date(message.created_at).toLocaleTimeString()}
                    </p>
                  </div>
                </div>
              ))}
            </ScrollArea>
            <div className="p-4 border-t flex gap-2">
              <Input
                placeholder="Type a message..."
                value={newMessage}
                onChange={(e) => setNewMessage(e.target.value)}
                onKeyPress={handleKeyPress}
                disabled={loading}
                className="focus:ring-2 focus:ring-primary"
                aria-label="Message input"
              />
              <Button 
                onClick={sendMessage} 
                disabled={loading || !newMessage.trim()}
                className="transition-all hover:scale-110 disabled:hover:scale-100"
                aria-label="Send message"
              >
                {loading ? (
                  <span className="h-4 w-4 animate-spin rounded-full border-2 border-background border-t-transparent" />
                ) : (
                  <Send className="h-4 w-4" />
                )}
              </Button>
            </div>
          </>
        ) : (
          <div className="flex-1 flex items-center justify-center text-muted-foreground animate-fade-in">
            <div className="text-center space-y-4">
              <Users className="h-12 w-12 mx-auto opacity-50" aria-hidden="true" />
              <p>Select a conversation or start a new one</p>
            </div>
          </div>
        )}
      </div>

      {/* Rename Dialog */}
      <Dialog open={showRenameDialog} onOpenChange={setShowRenameDialog}>
        <DialogContent aria-describedby="rename-description">
          <DialogHeader>
            <DialogTitle>Rename Conversation</DialogTitle>
            <p id="rename-description" className="sr-only">
              Enter a new name for this conversation
            </p>
          </DialogHeader>
          <div className="space-y-4">
            <Input
              placeholder="Enter new name..."
              value={newConversationName}
              onChange={(e) => setNewConversationName(e.target.value)}
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  handleRenameConversation();
                }
              }}
              className="focus:ring-2 focus:ring-primary"
              aria-label="New conversation name"
              autoFocus
            />
            <div className="flex justify-end gap-2">
              <Button 
                variant="outline" 
                onClick={() => setShowRenameDialog(false)}
                className="transition-all hover:scale-105"
              >
                Cancel
              </Button>
              <Button 
                onClick={handleRenameConversation} 
                disabled={!newConversationName.trim()}
                className="transition-all hover:scale-105 disabled:hover:scale-100"
              >
                Save
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Dialog */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Conversation</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this conversation? This will delete all messages and cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="transition-all hover:scale-105">
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleDeleteConversation} 
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90 transition-all hover:scale-105"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
